import {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react'
import {
    IBookingFormContext,
    IBookingFormObj,
    IBookingFormProviderProps,
} from './types'
import { useSelect } from '@wordpress/data'
import { store_name } from '../../../store/frontend'
import { IServiceProps } from '../../components/Services/types'
import { IFormData } from '../../screens/BookingForm/types'
import { ICategory } from '../../components/Categories/types'
import { IFieldConstructor } from '../../components/Form/types'
import { constructFormData } from './utils'
import generateColorShades from '../../lib/colorShades'

export const BookingContext = createContext<IBookingFormContext | null>(null)

export const useBookingContext = () => {
    const context = useContext(BookingContext)

    if (!context) {
        throw new Error(
            'useBookingContext must be used within a BookingContextProvider'
        )
    }

    return { ...context } as IBookingFormContext
}

export const BookingFormProvider = ({
    attrService,
    attrCategory,
    preset: customPreset,
    children,
}: PropsWithChildren<IBookingFormProviderProps>) => {
    // Separate customPreset logic from useSelect
    const storePreset = useSelect(
        (select: any) => select(store_name).getPreset(),
        []
    )

    const preset = customPreset || storePreset

    const {
        services: allServices = [],
        categories: allCategories = [],
        settings = {},
        appearance = [],
    } = preset || {}

    const {
        date_format = 'F j, Y',
        time_format = 'g:i a',
        timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
        price_format = '$#price',
    } = settings

    const extractedAttrCats =
        attrCategory && String(attrCategory).length > 0 && attrCategory !== '0'
            ? String(attrCategory)
                  .split(',')
                  .map((cat) => Number(cat))
            : []

    const [formObj, setFormObj] = useState<IBookingFormObj>({
        categories: [] as ICategory[],
        services: [] as IServiceProps[],
        preset: {},
        attrService: null,
        attrCategory: null,
        extractedAttrCats: [],
        formData: {} as IFormData,
        dateFormat: 'F j, Y',
        timeFormat: 'g:i a',
        priceFormat: '$#price',
        fields: [] as IFieldConstructor[],
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        paymentMethods: [],
        amountData: {
            total: 0,
            tax: 0,
            discount: 0,
            subtotal: 0,
            items: [],
        },
        colors: {
            primary: {},
            secondary: {},
        },
    })

    const onCategorySelect = useCallback((id: number) => {
        setFormObj((prev) => {
            return {
                ...prev,
                formData: {
                    ...prev.formData,
                    category: id,
                },
                categories: prev.categories.map((category: any) => {
                    if (category.id === id) {
                        return {
                            ...category,
                            selected: !category.selected,
                        }
                    }

                    return {
                        ...category,
                        selected: false,
                    }
                }),
            }
        })
    }, [])

    const onServiceUpdate = useCallback(
        (id: number, serviceProps: Partial<IServiceProps>) => {
            setFormObj((prev) => {
                const updatedServices = prev.services.map((service: any) => {
                    if (service.id === id) {
                        if ('selected' in serviceProps) {
                            const isSelecting = serviceProps.selected === true
                            return {
                                ...service,
                                ...serviceProps,
                                selectedAt: isSelecting
                                    ? service.selectedAt || Date.now()
                                    : null,
                                places: isSelecting ? service.places : [],
                            }
                        }
                        return {
                            ...service,
                            ...serviceProps,
                        }
                    }
                    return {
                        ...service,
                        selected:
                            preset?.settings
                                ?.allowed_multiple_service_selection === false
                                ? false
                                : service.selected,
                    }
                })

                return {
                    ...prev,
                    services: updatedServices,
                    formData: {
                        ...prev.formData,
                        services: updatedServices
                            .filter(
                                (service: IServiceProps) => service.selected
                            )
                            .map((service: IServiceProps) => service.id),
                    },
                }
            })
        },
        [preset]
    )

    useEffect(() => {
        if (!allServices || !allCategories) return

        const services = allServices.map((service: IServiceProps) => {
            const isSelected = Number(attrService) === Number(service.id)
            return {
                ...service,
                selected: isSelected,
                selectedAt: isSelected ? Date.now() : null,
                quantity: service.min_quantity || 1,
                onUpdate: (serviceProps: Partial<IServiceProps>) =>
                    onServiceUpdate(service.id, serviceProps),
                selectedDate: new Date(),
                selectedMonth: new Date(),
                places: [],
                expanded: false,
            }
        })

        const categories = allCategories.map((category: any) => {
            return {
                ...category,
                selected: false,
                onSelect: () => onCategorySelect(category.id),
            }
        })

        // ✅ FIX: use functional form to avoid overwriting current state like slot selection
        setFormObj((prev) => ({
            ...prev,
            services,
            categories,
            preset,
            dateFormat: date_format,
            timeFormat: time_format,
            timezone,
            priceFormat: price_format,
        }))
    }, [
        allServices,
        allCategories,
        date_format,
        time_format,
        timezone,
        price_format,
        preset,
    ])

    useEffect(() => {
        if (appearance && appearance.length === 2) {
            setFormObj((prev) => ({
                ...prev,
                colors: {
                    primary: generateColorShades(appearance[0]),
                    secondary: generateColorShades(appearance[1]),
                },
            }))
        }
    }, [appearance])

    const setFormData = useCallback(
        (fieldName: string, fieldValue: unknown) => {
            setFormObj((prev) => ({
                ...prev,
                formData: {
                    ...prev.formData,
                    [fieldName]: fieldValue,
                },
            }))
        },
        []
    )

    const setFields = useCallback(
        (fields: IFieldConstructor[]) => {
            setFormObj((prev) => ({
                ...prev,
                fields,
            }))
        },
        [formObj.fields]
    )

    useEffect(() => {
        if (!formObj.services || !formObj.fields) return

        setFormObj((prev) => ({
            ...prev,
            formData: constructFormData(prev),
        }))
    }, [formObj.services, formObj.fields])

    const amountData = useSelect(
        (select: any) => select(store_name).getBookingAmounts(),
        []
    )

    const loading = useSelect(
        (select: any) => select(store_name).getLoading(),
        []
    )

    useEffect(() => {
        setFormObj((prev) => ({
            ...prev,
            amountData,
        }))
    }, [amountData])

    return (
        <BookingContext.Provider
            value={{
                ...formObj,
                setFormData,
                setFields,
                attrService,
                attrCategory,
                extractedAttrCats,
                loading,
                setFormObj(key, value) {
                    setFormObj((prev) => {
                        return {
                            ...prev,
                            [key]: value,
                        }
                    })
                },
            }}
        >
            {children}
        </BookingContext.Provider>
    )
}
